import { apiClient, extractApiError } from './apiClient';
import { secureStorage } from './secureStorage';
import { User } from '../types';

export interface LoginPayload {
  phone: string;
  password: string;
}

/** Documents required for identity verification (all users) */
export interface IdentityDocs {
  /** National ID card or passport scan (front) */
  id_document: File;
  /** Live selfie holding the ID/Passport */
  selfie_with_id: File;
}

/** Additional professional documents required for SURVEYOR accounts */
export interface SurveyorDocs {
  /** Tertiary certificate, qualification, diploma, or degree */
  tertiary_certificate: File;
  /** Resume or CV */
  resume_cv: File;
}

export type QualificationType = 'tertiary' | 'non_tertiary';

export interface RegisterPayload {
  email: string;
  phone: string;
  password: string;
  first_name: string;
  last_name: string;
  role: 'CLIENT' | 'SURVEYOR';
  identity_docs: IdentityDocs;
  surveyor_docs?: SurveyorDocs;
  terms_accepted: boolean;
  terms_version: string;
  qualification_type?: QualificationType;
  interview_requested?: boolean;
  contact_phone?: string;
}

export interface OtpVerifyPayload {
  phone?: string;
  email?: string;
  otp: string;
}

export interface OtpRequestResponse {
  detail?: string;
  sent_to?: string;
  identifier?: string;
  identifier_type?: 'email' | 'phone';
  requires_otp?: boolean;
  is_email_verified?: boolean;
  is_active?: boolean;
  pending_approval?: boolean;
}

export interface PasswordResetConfirmPayload {
  email: string;
  otp: string;
  new_password: string;
}

export interface AuthTokens {
  access: string;
  refresh: string;
}

const TOKEN_KEY = 'tokens';

export const authService = {
  async login(payload: LoginPayload): Promise<{ tokens: AuthTokens; user: User }> {
    const { data } = await apiClient.post('/auth/login/', payload);
    await secureStorage.setItem(TOKEN_KEY, JSON.stringify({ access: data.access, refresh: data.refresh }));
    return { tokens: { access: data.access, refresh: data.refresh }, user: data.user };
  },

  async register(payload: RegisterPayload): Promise<{ message: string }> {
    // Build multipart/form-data so document files are transmitted correctly
    try {
      // Friendly debug: surface terms_version and interview flag when submitting
      // (Left intentionally verbose for short-term debugging)
      // eslint-disable-next-line no-console
      console.debug('[authService] register payload preview', {
        email: payload.email,
        phone: payload.phone,
        role: payload.role,
        terms_accepted: payload.terms_accepted,
        terms_version: payload.terms_version,
        qualification_type: payload.qualification_type,
        interview_requested: payload.interview_requested,
        contact_phone: payload.contact_phone,
      });
    } catch (e) {
      // ignore
    }
    const form = new FormData();
    form.append('phone', payload.phone);
    form.append('email', payload.email);
    form.append('password', payload.password);
    form.append('first_name', payload.first_name);
    form.append('last_name', payload.last_name);
    form.append('role', payload.role);

    // Identity documents (required for all users)
    form.append('id_document', payload.identity_docs.id_document);
    form.append('selfie_with_id', payload.identity_docs.selfie_with_id);

    form.append('terms_accepted', String(payload.terms_accepted));
    form.append('terms_version', payload.terms_version);

    if (payload.role === 'SURVEYOR') {
      form.append('qualification_type', payload.qualification_type || 'tertiary');
      form.append('interview_requested', String(payload.interview_requested || false));
      if (payload.contact_phone) {
        form.append('contact_phone', payload.contact_phone);
      }
      if (payload.surveyor_docs) {
        form.append('tertiary_certificate', payload.surveyor_docs.tertiary_certificate);
        form.append('resume_cv', payload.surveyor_docs.resume_cv);
      }
    }

    const { data } = await apiClient.post('/auth/register/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async verifyOtp(payload: OtpVerifyPayload): Promise<{ tokens: AuthTokens; user: User }> {
    try {
      // eslint-disable-next-line no-console
      console.debug('[authService] verifyOtp payload', { phone: payload.phone, email: payload.email, otp: payload.otp });
    } catch (e) {
      // ignore
    }
    const body: any = { otp_code: payload.otp };
    if (payload.email) body.email = payload.email;
    else if (payload.phone) body.phone = payload.phone;
    const { data } = await apiClient.post('/auth/otp/verify/', body);
    // OTP verifies the account; the user should still sign in explicitly afterward.
    if (data?.refresh && data?.access) {
      return { tokens: { access: data.access, refresh: data.refresh }, user: data.user };
    }
    return { tokens: { access: '', refresh: '' }, user: data.user };
  },

  async requestOtp(identifier: string): Promise<OtpRequestResponse> {
    const trimmed = identifier.trim();
    const payload = trimmed.includes('@') ? { email: trimmed } : { phone: trimmed };
    const { data } = await apiClient.post('/auth/otp/request/', payload);
    return data;
  },

  async requestPasswordReset(email: string): Promise<void> {
    await apiClient.post('/auth/password-reset/', { email });
  },

  async confirmPasswordReset(payload: PasswordResetConfirmPayload): Promise<void> {
    await apiClient.post('/auth/password-reset/confirm/', {
      email: payload.email,
      otp_code: payload.otp,
      new_password: payload.new_password,
    });
  },

  async refreshToken(): Promise<string | null> {
    try {
      const raw = await secureStorage.getItem(TOKEN_KEY);
      if (!raw) return null;
      const { refresh } = JSON.parse(raw) as AuthTokens;
      const { data } = await apiClient.post('/auth/refresh/', { refresh });
      const tokens: AuthTokens = { access: data.access, refresh };
      await secureStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
      return data.access;
    } catch {
      await secureStorage.removeItem(TOKEN_KEY);
      return null;
    }
  },

  async getStoredTokens(): Promise<AuthTokens | null> {
    try {
      const raw = await secureStorage.getItem(TOKEN_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as AuthTokens;
    } catch {
      return null;
    }
  },

  async getProfile(): Promise<User> {
    const { data } = await apiClient.get('/users/me/');
    return data;
  },

  async logout(): Promise<void> {
    try {
      const tokens = await this.getStoredTokens();
      if (tokens?.refresh) {
        await apiClient.post('/auth/logout/', { refresh: tokens.refresh }).catch(() => { });
      }
    } finally {
      await secureStorage.removeItem(TOKEN_KEY);
    }
  },
};
